import React, { useContext, useState, useEffect } from "react";
import { clsx } from "clsx";
import { type InjectedViewportProps, handleViewport } from "react-in-viewport";
import { useSelector } from "react-redux";
import { Dialog } from "@bloomberg/media-ui-overlay";
import { FortressProvider } from "@foundation/fortress";
import { QuickAddProvider } from "@foundation/quickadd-provider";
import { useRegUIClient, SubscriptionsData } from "@foundation/reg-ui-client";
import { ScreenContext } from "@foundation/screen";
import AdOptionsProvider from "@foundation/sparkle/lib/AdOptionsProvider";

import { AdConfig } from "~api/getAdConfig";
import AbbaMetadataScript from "~components/AbbaMetadataScript";
import useResetAdContext from "~components/ads/hooks/useResetAdContext";
import useUpdateCorrelator from "~components/ads/hooks/useUpdateCorrelator";
import Basic, { Storythread } from "~components/articles/basic/Basic";
import Feature from "~components/articles/feature/Feature";
import BottomArticleAd from "~components/BottomArticleAd";
import ErrorBoundary from "~components/ErrorBoundary";
import GridLayout from "~components/GridLayout";
import { TransporterObserver } from "~components/TransporterObserver";
import useUpdateStoryInViewportMetadata from "~hooks/useUpdateStoryInViewportMetadata";
import {
    selectCurrentStory,
    selectStories,
} from "~redux/features/stories-slice";
import { selectCurrentSlug } from "~redux/features/transporter-slice";
import { ARTICLE_TYPE, Story as StoryType } from "~types/types";
import { CurrentStoryContext } from "~utils/currentStoryContext";
import getArticleMetadata from "~utils/getArticleMetadata";
import getArticleType from "~utils/getArticleType";
import { getFortressPropsFromStory } from "~utils/getFortressPropsFromStory";
import getShouldShowContextualNav from "~utils/getShouldShowContextualNav";
import { TranslatedMessage, useTranslateMessage } from "~utils/i18n/translate";
import { sparkleAdOptions } from "~utils/sparkleOptions";

import styles from "./articleLayout.module.css";

const LOGIN_REVERSE_PROXY_URL = process.env.NEXT_PUBLIC_REGISTRATION_URL;
const WATCHLIST_SERVICE_URL = `${LOGIN_REVERSE_PROXY_URL}/watchlist-service`;

export interface ArticleLayoutProps {
    story: StoryType;
    preview: boolean;
    adConfig: AdConfig;
}
const PAYWALL_TYPE = "additive";
const CORPORATE_HUB_DIALOG_TIP_KEY = "CORPORATE_HUB_DIALOG";

function ArticleLayout({
    story,
    preview,
    adConfig,
    inViewport,
    forwardedRef,
}: ArticleLayoutProps & InjectedViewportProps) {
    const stories = useSelector(selectStories);
    const currentStory = useSelector(selectCurrentStory);
    const isFirstStoryOnPage = stories[0]?.slug === story.slug;
    const currentSlug = useSelector(selectCurrentSlug);
    const inView = currentSlug === story.slug;
    const TRANSPORTER_ENABLED = !preview;
    const {
        breakpoints: { smallDesktop, largeDesktop },
    } = useContext(ScreenContext);
    const translateMessage = useTranslateMessage();

    const { client } = useRegUIClient();
    const userSubscriptionsData: SubscriptionsData =
        client?.getSubscriberData()?.subscriptionsData;
    const [showCoporateHubTip, setShowCoporateHubTip] = useState(false);

    useEffect(() => {
        const hasHubAccess = userSubscriptionsData?.some(
            (sub) => sub.corporateHubAccess
        );

        if (!smallDesktop && !largeDesktop) return;
        if (!hasHubAccess) return;

        const storedCorporateHubMap = JSON.parse(
            localStorage.getItem(CORPORATE_HUB_DIALOG_TIP_KEY) || "{}"
        );

        if (!storedCorporateHubMap.dismissed) {
            if (!storedCorporateHubMap.viewDate) {
                const value = { viewDate: Date.now() };
                localStorage.setItem(
                    CORPORATE_HUB_DIALOG_TIP_KEY,
                    JSON.stringify(value)
                );
                setShowCoporateHubTip(true);
            } else {
                const oneDayAgo = 24 * 60 * 60 * 1000;
                const currentDate = Date.now();
                if (
                    currentDate - Number(storedCorporateHubMap.viewDate) <
                    oneDayAgo
                ) {
                    setShowCoporateHubTip(true);
                }
            }
        }
    }, [largeDesktop, smallDesktop, userSubscriptionsData]);

    useUpdateCorrelator(inViewport);
    useResetAdContext(inViewport, story);
    useUpdateStoryInViewportMetadata({ story, inViewport, isFirstStoryOnPage });

    const articleType = getArticleType(story);
    const fortressOptions = getFortressPropsFromStory(story, PAYWALL_TYPE);
    const [storythread] = story.storythreads || [];

    const shouldShowContextualNav = getShouldShowContextualNav(currentStory);
    const cx = clsx({
        "first-story": isFirstStoryOnPage,
        current: inView,
        [styles.basic]: articleType === ARTICLE_TYPE.BASIC,
    });

    function onCorporateHubTipDismiss() {
        const storedCorporateHubMap = JSON.parse(
            localStorage.getItem(CORPORATE_HUB_DIALOG_TIP_KEY) || "{}"
        );
        const value = { ...storedCorporateHubMap, dismissed: true };

        localStorage.setItem(
            CORPORATE_HUB_DIALOG_TIP_KEY,
            JSON.stringify(value)
        );
    }

    function onCorporateHubTipShowMe() {
        onCorporateHubTipDismiss();
        window.open("/subscriptions/group/hub?in_source=tip", "_self");
    }

    return (
        <FortressProvider options={fortressOptions} inView={inView}>
            <CurrentStoryContext.Provider
                value={{ story, articleType }}
                key={story.id}
            >
                <AdOptionsProvider
                    adOptions={sparkleAdOptions(story, adConfig)}
                    articleMetadata={getArticleMetadata(story)}
                    customRefreshOptions={adConfig?.customRefreshOptions}
                >
                    {storythread && !shouldShowContextualNav && (
                        <Storythread
                            storythread={storythread}
                            className={styles[articleType]}
                        />
                    )}
                    <ErrorBoundary
                        fallback={translateMessage({
                            id: "articleRenderFailed",
                            defaultMessage: "Failed to render article",
                        })}
                    >
                        <QuickAddProvider
                            watchlistServiceUrl={WATCHLIST_SERVICE_URL}
                        >
                            <GridLayout
                                ref={forwardedRef}
                                id={story?.id}
                                className={cx}
                            >
                                <AbbaMetadataScript />

                                {articleType === ARTICLE_TYPE.BASIC && (
                                    <Basic story={story} />
                                )}

                                {(articleType === ARTICLE_TYPE.FEATURE ||
                                    articleType ===
                                        ARTICLE_TYPE.EXPLAINERS) && (
                                    <Feature story={story} />
                                )}
                            </GridLayout>
                        </QuickAddProvider>
                    </ErrorBoundary>

                    {TRANSPORTER_ENABLED && <TransporterObserver />}

                    {showCoporateHubTip && (smallDesktop || largeDesktop) && (
                        <Dialog
                            containerClassName={styles.corporateHubTip}
                            Icon={
                                <span className={styles.badge}>
                                    <TranslatedMessage
                                        id="new"
                                        defaultMessage="NEW"
                                    />
                                </span>
                            }
                            eyebrow={translateMessage({
                                id: "myCompanyHub",
                                defaultMessage: "My Company Hub",
                            })}
                            ctaText={translateMessage({
                                id: "showMe",
                                defaultMessage: "Show me",
                            })}
                            text={translateMessage({
                                id: "corpSubscribersPage",
                                defaultMessage:
                                    "A new page exclusively for corporate subscribers",
                            })}
                            subtext={translateMessage({
                                id: "corpSubscribersBrowse",
                                defaultMessage:
                                    "Browse Bloomberg.com content handpicked specifically for your organization.",
                            })}
                            onCta={onCorporateHubTipShowMe}
                            onDismiss={onCorporateHubTipDismiss}
                        />
                    )}

                    <BottomArticleAd />

                    <div className="custom-banner" />
                </AdOptionsProvider>
            </CurrentStoryContext.Provider>
        </FortressProvider>
    );
}

const ArticleLayoutWithViewportListener = handleViewport(
    ArticleLayout,
    { threshold: 0 },
    { disconnectOnLeave: false }
);

export default ArticleLayoutWithViewportListener;
